import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import CRMApi from '../../../Api/CRM/CRMApi';
import GetDictionaryResponse from '../../../Api/CRM/Responses/DictionaryResponse';
import Menu from '../../../Api/Models/Menu';
import MenuItem from '../../../Api/Models/MenuItem';
import TrueFalseIcon from '../../../components/common/TrueFalseIcon';
import useGlobalState from '../../GlobalStateContext';
import ActionButton from '../../elements/ActionButton';
import DeleteButton from '../../elements/DeleteButton';
import EditButton from '../../elements/EditButton';
import Search from '../../elements/Search';
import ViewButton from '../../elements/ViewButton';
import Card from '../../elements/common/Card';
import SortButton from '../../elements/common/SortButton';
import Row, { TableCell as Cell, TableDetailsBody } from '../../elements/common/TableDetailsRow';
import TableHead from '../../elements/common/TableHead';
import TableLoader from '../../elements/common/TableLoader';
import Paginator from '../../elements/paginator';
import useAutoPaginator, { UpdateItemsFunction } from '../../hooks/useAutoPaginator';
import useNumberParams from '../../hooks/useNumberParams';
import useOnce from '../../hooks/useOnce';
import {PERMISSION} from "../../helpers/Permissions";

export default function MenuDetails() {
    const { languageNames } = useGlobalState();
    const { id } = useNumberParams();
    const [menu, setMenu] = useState<Menu>({} as Menu);
    const [translatables, setTranslatables] = useState<GetDictionaryResponse>({});

    useOnce(() => {
        CRMApi.menus.getById(id).then(setMenu);
        CRMApi.menus.getTranslatableNames().then(setTranslatables);
    });

    const { items, updateItems, isLoading, auto } = useAutoPaginator<MenuItem>((page, params) =>
        CRMApi.menus.getMenuItems(id, page, { params })
    );

    return (
        <>
            <Card title="Menu details">
                <TableDetailsBody>
                    <Row label="ID">{menu.id}</Row>
                    <Row label="Name">{menu.name}</Row>
                    <Row label="Slug">{menu.slug}</Row>
                    <Row label="Language">{languageNames[menu.language_id]}</Row>
                    <Row label="Translation of">{translatables[menu.translation_id]}</Row>
                </TableDetailsBody>
                <Link to={`/menus/${id}/update`} className="btn btn-default right">
                    Edit Menu
                </Link>
            </Card>
            <ActionButton
                url={`/menu_items/create/${menu.id}`}
                title="Create new Menu Item"
                permissions={[PERMISSION.CREATE_MENU_ITEM]}
            />
            <Card title="Menu items">
                <Search {...auto.search} />
                <TableLoader isLoading={isLoading}>
                    <table className="table model-list">
                        <TableHead>
                            <br />
                            <SortButton column="id" {...auto.sort} />
                            <SortButton column="order" {...auto.sort} />
                            <SortButton column="name" {...auto.sort} />
                            <SortButton column="url" {...auto.sort} />
                            <SortButton column="class" {...auto.sort} />
                            <SortButton column="rel_nofollow" label="No follow" {...auto.sort} />
                            <SortButton column="parent_id" {...auto.sort} />
                        </TableHead>
                        <MenuItemsTableBody items={items} update={updateItems} />
                    </table>
                    <Paginator {...auto.paginator} />
                </TableLoader>
            </Card>
        </>
    );
}

function MenuItemsTableBody({ items, update }: { items: MenuItem[]; update: UpdateItemsFunction }) {
    return (
        <tbody>
            {items.map((item) => (
                <tr key={item.id}>
                    <td>
                        <ViewButton url={`/menu_items/${item.id}`} permissions={[PERMISSION.VIEW_MENU_ITEM]} />
                        <EditButton url={`/menu_items/${item.id}/update`} permissions={[PERMISSION.UPDATE_MENU_ITEM]} />
                        <DeleteButton
                            url={`/crea/api/menu_items/${item.id}`}
                            action={update}
                            permissions={[PERMISSION.DELETE_MENU_ITEM]}
                        />
                    </td>
                    <td>{item.id}</td>
                    <td>{item.order}</td>
                    <td>{item.name}</td>
                    <Cell>{item.url}</Cell>
                    <Cell>{item.class}</Cell>
                    <td>
                        <TrueFalseIcon status={item.rel_nofollow} />
                    </td>
                    <Cell>{item.parent_id}</Cell>
                </tr>
            ))}
        </tbody>
    );
}
